import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Register.css";

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { register, isLoggedIn, authLoading, authError, clearError } = useAuth();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    billingEmail: "",
    password: "",
    confirmPassword: "",
    marketingOptIn: false,
    agreeTerms: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [localMessage, setLocalMessage] = useState("");

  const redirectTo = useMemo(() => {
    return searchParams.get("redirect") || location.state?.from?.pathname || "/account";
  }, [searchParams, location.state]);

  useEffect(() => {
    document.title = "Create Account | MayaChitro";

    if (isLoggedIn) {
      navigate("/account", { replace: true });
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    clearError();
    setLocalMessage("");
  }, [clearError]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (localMessage) setLocalMessage("");
    if (authError) clearError();
  };

  const validateForm = () => {
    const fullName = formData.fullName.trim();
    const email = formData.email.trim().toLowerCase();
    const billingEmail = formData.billingEmail.trim().toLowerCase();
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;

    if (!fullName) return "Please enter your full name.";
    if (!email) return "Please enter your email address.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Please enter a valid email address.";
    }

    if (billingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail)) {
      return "Please enter a valid billing email address.";
    }

    if (password.length < 8) {
      return "Password must be at least 8 characters.";
    }

    if (password !== confirmPassword) {
      return "Password and confirm password do not match.";
    }

    if (!formData.agreeTerms) {
      return "Please agree to the account terms before creating your account.";
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationMessage = validateForm();

    if (validationMessage) {
      setLocalMessage(validationMessage);
      return;
    }

    const result = await register({
      fullName: formData.fullName.trim(),
      email: formData.email.trim().toLowerCase(),
      billingEmail:
        formData.billingEmail.trim().toLowerCase() ||
        formData.email.trim().toLowerCase(),
      password: formData.password,
      marketingOptIn: formData.marketingOptIn,
    });

    if (result.success) {
      navigate(redirectTo, { replace: true });
    } else {
      setLocalMessage(result.message || "Could not create account. Please try again.");
    }
  };

  const messageToShow = authError || localMessage;

  return (
    <main className="register-page">
      <section className="register-shell">
        <div className="register-brand-panel">
          <p className="register-eyebrow">Create MayaChitro Account</p>

          <h1>Save your favorite visuals and access downloads anytime.</h1>

          <p>
            Create a free account to save wishlist items, view order history,
            manage purchased downloads, and choose whether you want product updates.
          </p>

          <div className="register-benefits">
            <div>
              <span>Wishlist</span>
              <strong>Save photos and wall art before buying.</strong>
            </div>

            <div>
              <span>Orders</span>
              <strong>View purchase history and download access.</strong>
            </div>

            <div>
              <span>Updates</span>
              <strong>Receive new releases only when you choose to opt in.</strong>
            </div>
          </div>
        </div>

        <div className="register-card">
          <div className="register-card-heading">
            <p className="register-eyebrow">Account Setup</p>
            <h2>Create your account</h2>
            <p>
              Use email registration now. Google and Facebook login can be connected later.
            </p>
          </div>

          <div className="social-register-row">
            <button type="button" disabled>
              <span>G</span>
              Continue with Google
              <small>Coming soon</small>
            </button>

            <button type="button" disabled>
              <span>f</span>
              Continue with Facebook
              <small>Coming soon</small>
            </button>
          </div>

          <div className="register-divider">
            <span>or create with email</span>
          </div>

          {messageToShow && <div className="register-alert">{messageToShow}</div>}

          <form className="register-form" onSubmit={handleSubmit}>
            <label>
              Full name
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Ariful Islam Shohag"
                autoComplete="name"
                required
              />
            </label>

            <label>
              Email address
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </label>

            <label>
              Billing email <small>Optional</small>
              <input
                type="email"
                name="billingEmail"
                value={formData.billingEmail}
                onChange={handleChange}
                placeholder="Use only if different from login email"
                autoComplete="email"
              />
            </label>

            <label>
              Password
              <div className="register-password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                  required
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </label>

            <label>
              Confirm password
              <input
                type={showPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your password"
                autoComplete="new-password"
                required
              />
            </label>

            <label className="register-check-row">
              <input
                type="checkbox"
                name="marketingOptIn"
                checked={formData.marketingOptIn}
                onChange={handleChange}
              />
              <span>
                Send me new photo stories, wall art releases, discounts, and
                MayaChitro updates.
              </span>
            </label>

            <label className="register-check-row">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleChange}
                required
              />
              <span>
                I agree to create a MayaChitro account and understand that marketing
                emails are optional.
              </span>
            </label>

            <button className="register-submit" type="submit" disabled={authLoading}>
              {authLoading ? "Creating account..." : "Create Account"}
            </button>
          </form>

          <div className="register-footer-note">
            <span>Already have an account?</span>
            <Link to="/login">Login</Link>
          </div>

          <p className="register-privacy-note">
            Your email is used for account access, order history, download access,
            and support. Product updates are sent only when you opt in.
          </p>
        </div>
      </section>
    </main>
  );
}